import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, Mail, Facebook, Instagram, Music, Link2, MapPin, MessageCircle, ArrowRight, ChevronDown } from 'lucide-react';
import { trackNavigation, trackButtonClick } from '../lib/analytics';

const Footer = () => {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const services = [
    { name: 'Websites & Web Platforms', path: '/services#websites' },
    { name: 'Applications & SaaS', path: '/services#applications' },
    { name: 'AI & Automation', path: '/services#automation' },
    { name: 'Marketing, SEO & Growth', path: '/services#marketing' },
    { name: 'Support & Management', path: '/services#support' },
  ];

  const companyLinks = [
    { name: 'Showcase', path: '/showcase' },
    { name: 'About / Process', path: '/about' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  const socialLinks = [
    {
      name: 'Facebook',
      href: 'https://www.facebook.com/profile.php?id=61573912922638',
      icon: Facebook,
    },
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/aiwebsolutionsiom/',
      icon: Instagram,
    },
    {
      name: 'TikTok',
      href: 'https://www.tiktok.com/@ai.websolutions',
      icon: Music,
    },
    {
      name: 'Links',
      href: 'https://aiwebsolutions.social/',
      icon: Link2,
    },
  ];

  const handleStartProject = () => {
    trackButtonClick('Start Your Project', 'Footer');
    navigate('/contact');
  };

  return (
    <footer className="relative z-10 border-t border-white/[0.06]">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-16">
          <div className="col-span-1 md:col-span-2 lg:col-span-1">
            <Link to="/" className="text-lg font-medium text-white">
              AI & Web Solutions IOM
            </Link>
            <p className="mt-4 text-sm text-gray-500 leading-relaxed">
              Websites, applications & automation systems for Isle of Man and UK businesses.
            </p>
            <button
              onClick={handleStartProject}
              className="mt-6 inline-flex items-center px-5 py-2.5 text-sm font-medium text-black bg-white rounded-xl hover:bg-white/90 transition-all duration-300"
            >
              Start Your Project
              <ArrowRight className="ml-2 h-4 w-4" />
            </button>
          </div>

          <div>
            <button
              onClick={() => toggleSection('services')}
              className="lg:pointer-events-none flex items-center justify-between w-full text-xs font-medium text-gray-400 uppercase tracking-wider mb-5 lg:cursor-default"
            >
              Services
              <ChevronDown className={`h-4 w-4 lg:hidden transition-transform ${expandedSection === 'services' ? 'rotate-180' : ''}`} />
            </button>
            <ul className={`space-y-3.5 ${expandedSection === 'services' ? 'block' : 'hidden lg:block'}`}>
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.path}
                    onClick={() => trackNavigation(service.path, service.name)}
                    className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <button
              onClick={() => toggleSection('company')}
              className="lg:pointer-events-none flex items-center justify-between w-full text-xs font-medium text-gray-400 uppercase tracking-wider mb-5 lg:cursor-default"
            >
              Company
              <ChevronDown className={`h-4 w-4 lg:hidden transition-transform ${expandedSection === 'company' ? 'rotate-180' : ''}`} />
            </button>
            <ul className={`space-y-3.5 ${expandedSection === 'company' ? 'block' : 'hidden lg:block'}`}>
              {companyLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    onClick={() => trackNavigation(link.path, link.name)}
                    className="text-sm text-gray-500 hover:text-gray-300 transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-5">
              Contact
            </h3>
            <ul className="space-y-3.5">
              <li>
                <a
                  href="https://wa.me/4407624474095"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackButtonClick('WhatsApp', 'Footer')}
                  className="flex items-center text-sm text-gray-500 hover:text-gray-300 transition-colors"
                >
                  <MessageCircle className="h-4 w-4 mr-2.5" />
                  WhatsApp / DM
                </a>
              </li>
              <li>
                <a
                  href="mailto:aiwebsolutionsiom@gmail.com"
                  className="flex items-center text-sm text-gray-500 hover:text-gray-300 transition-colors"
                >
                  <Mail className="h-4 w-4 mr-2.5" />
                  Email us
                </a>
              </li>
              <li className="flex items-center text-sm text-gray-500">
                <MapPin className="h-4 w-4 mr-2.5" />
                Isle of Man
              </li>
            </ul>

            <div className="mt-8">
              <div className="flex gap-2">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackButtonClick(social.name, 'Footer Social')}
                    className="w-9 h-9 rounded-lg bg-white/[0.03] border border-white/[0.06] flex items-center justify-center text-gray-500 hover:text-gray-300 hover:border-white/10 transition-all duration-300"
                    aria-label={`Follow us on ${social.name}`}
                  >
                    <social.icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/[0.06]">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-600">
              &copy; {new Date().getFullYear()} AI & Web Solutions IOM. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="/privacy"
                onClick={() => trackNavigation('/privacy', 'Privacy Policy')}
                className="text-sm text-gray-600 hover:text-gray-400 transition-colors"
              >
                Privacy Policy
              </Link>
              <span className="text-gray-700">|</span>
              <span className="text-sm text-gray-600">Terms</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
